import { ChevronRight, Delete, Edit } from '@mui/icons-material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Autocomplete, Button, FormControl, IconButton, InputLabel, List, ListItem, TextField, TextFieldProps } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { debounce } from '@mui/material/utils';
import { AppModels, SearchResult, appDispatch, appRegistry, jsonGet, objectToQueryString } from 'cdx-common';
import React from 'react';
import { AppItem } from './AppItems';

const searchThrottle: number = 200;

export interface SearchItem {
    type: AppModels,
    id: ModelId;
}

export const EditableSearchElement = (props: {
    autoFocusOnEmpty?: boolean,
    value?: SearchItem,
    filter?: AppModels[],
    onItemSelected?: (type: AppModels, id: ModelId, data: any) => void,
    onCreate?: (text: string) => void,
    closeOnBlur?: boolean;
    defaultValues?: SearchItem[];
    onEmpty?: () => React.ReactNode;
    textfieldProps?: Omit<TextFieldProps, 'variant'>,
}) => {
    const [editMode, setEditMode] = React.useState(props.value === undefined && props.autoFocusOnEmpty === true);
    React.useEffect(() => {
        setEditMode(props.value === undefined && props.autoFocusOnEmpty === true);
    }, []);
    return editMode ? <SuperSearchInput {...props} closeOnBlur /> : <List dense>
        <ListItem secondaryAction={
            <Button size='small' onClick={() => { setEditMode(false); }}><Edit /></Button>
        }>
            {props.value !== undefined ? <AppItem mode='full' onEdit={() => { setEditMode(true); }} id={props.value.id} type={props.value.type} /> : (props.onEmpty ? props.onEmpty() : null)}
        </ListItem>
    </List>;
};

const newItemToOptions = (label: string): SearchResult => {
    const key = `~${Math.round(Math.random() * 99999999)}` as ModelId;
    return { id: key, model: 'new' as AppModels, data: { id: key, name: label } as any };
};

export const SuperSearchInput = (props: {
    autoFocus?: boolean,
    value?: SearchItem | string,
    filter?: AppModels[],
    onItemSelected?: (type: AppModels, id: ModelId, data: any) => void,
    onCreate?: (text: string) => void,
    closeOnBlur?: boolean;
    createOnEndEditing?: boolean;
    defaultValues?: SearchItem[];
    textfieldProps?: TextFieldProps;
    clearOnSelect?: boolean,
    onSetEmpty?: () => any,
    onInputChange?: (newValue: string) => void,
    alwaysEditing?: boolean;
    placeholder?: string;
    onGotoElement?: () => void;
}) => {

    const [value, setValue] = React.useState<SearchResult | null>(null);
    const [inputValue, setInputValue] = React.useState('');
    const [options, setOptions] = React.useState<readonly SearchResult[]>([]);
    const [editing, setEditing] = React.useState(false);
    const [autofocus, setAutofocus] = React.useState(false);

    React.useEffect(() => {
        setEditing(props.value === undefined);
        setAutofocus(!!props.value);
    }, []);

    React.useEffect(() => {
        console.log('options changed', options);
    }, [options]);
    /*
    React.useEffect(() => {
        if (props.value) {
            console.log('props.value', props.value);
            setEditing(false);
        }
    }, [props.value]);
    */

    const fetch = React.useMemo(
        () =>
            debounce(
                async (
                    request: { input: string; },
                    callback: (results?: readonly SearchResult[]) => void,
                ) => {
                    const queryString = objectToQueryString({ ...props.filter ? { filterModels: props.filter, query: request.input } : { query: request.input } });
                    const items: SearchResult[] = await jsonGet('/data/search?' + queryString);
                    items.forEach(p => {
                        const s = appRegistry.get(p.model)?.slice?.actions?.upsertOne;
                        if (s) appDispatch(s(p.data));
                    });
                    callback(items);
                },
                searchThrottle,
            ),
        [],
    );

    const cancelEditing = React.useCallback((force?: boolean) => {

        if (props.alwaysEditing || (!props.value && force !== true)) {
            console.log('NO cancelEditing');
            return;
        }
        console.log('cancelEditing');

        setEditing(false);
        setInputValue('');
        setValue(null);
        setOptions([]);
    }, [props.alwaysEditing]);

    React.useEffect(() => {
        props.onInputChange && props.onInputChange(inputValue);
    }, [inputValue]);

    React.useEffect(() => {
        let active = true;



        if (inputValue === '') {
            setOptions(value ? [value] : []);
            return undefined;
        }

        fetch({ input: inputValue }, (results?: readonly SearchResult[]) => {
            if (active) {
                let newOptions: readonly SearchResult[] = [];

                if (value) {
                    newOptions = [value];
                }

                if (results) {
                    newOptions = [...newOptions, ...results];
                }

                if (props.onCreate && inputValue) {
                    newOptions = [...newOptions, newItemToOptions(inputValue)];
                }


                setOptions(newOptions);
            }
        });

        return () => {
            active = false;
        };
    }, [value, inputValue, fetch]);

    return editing || props.alwaysEditing === true ? <Autocomplete

        getOptionLabel={(option: SearchResult) => {
            if (props.clearOnSelect === true) {
                return '';
            } else {
                return typeof option === 'string' ? option : option.data.name;
            }
        }}
        filterOptions={(x) => x}
        options={options}
        forcePopupIcon={false}
        clearOnBlur={false}
        blurOnSelect={false}
        autoComplete
        includeInputInList
        filterSelectedOptions
        value={value ? value : null}
        noOptionsText={null}

        onChange={(_event: any, newValue: SearchResult | null) => {
            //setOptions(newValue ? [newValue, ...options] : options);

            // CHAT GPT
            if (newValue?.model === ('new' as AppModels) && props.onCreate) {
                props.onCreate(newValue.data.name);
                cancelEditing(true);
                return;
            }

            if (newValue && props.onItemSelected) {
                props.onItemSelected(
                    newValue.model,
                    String(newValue.id) as ModelId,
                    newValue.data,
                );
                cancelEditing(true);
            }
            /*
            if (newValue?.model && (props.filter === undefined || props.filter.includes(newValue?.model))) {
                if (newValue && props.onItemSelected) {
                    props.onItemSelected(newValue.model, String(newValue.id) as ModelId, newValue.data);
                    // if the value selectet is the same, the parent component will still
                    // stay editing because value doesn't switch. Do it manually here
                    if (!props.alwaysEditing) {
                        setEditing(false);
                    }
                }
                // pas mal weird ça. C'est le mopyen trouvé pour
                // le champ de recherche principal, où on souhaite
                // seulement sélectionner un truc pour ensuite naviguer,
                // pas nécessairement afficher la valeur de l'élément
                // sélectionné dans le champ texte !
                if (props?.clearOnSelect === true) {
                    setValue(null);
                    setInputValue('');
                    setOptions([]);
                } else {
                    setValue(newValue);
                }
            } else if (newValue && (newValue?.model as any) == 'new' && props.onCreate) {
                props.onCreate(newValue.data.name);
            }
            */
        }}
        onClose={(_event, reason) => {
            console.log('reason', reason);
            if (
                (reason === 'blur' || reason === 'escape')
            ) {
                cancelEditing();
            }
        }}
        onInputChange={(_event, newInputValue) => {
            setInputValue(newInputValue);
        }}
        renderInput={(params) => (
            <TextField
                label={props.placeholder ?? "Rechercher..."}
                {...props.textfieldProps}
                {...params}
                fullWidth
                onBlur={() => {
                    cancelEditing();
                }}
                slotProps={{
                    input: {
                        ...props.textfieldProps?.slotProps?.input,
                        ...params.InputProps,
                        autoFocus: autofocus,
                        endAdornment: props.value && props.alwaysEditing !== true ? <Button size='small' onClick={() => { cancelEditing(); }}>Annuler</Button> : undefined
                    }
                }}
            />
        )}
        renderOption={(props, option) => {
            const p = { ...props, key: option.model + '-' + option.id };
            console.log('props.key', p.key);
            return <li {...p}>{
                (option.model as any) == 'new' ?
                    <Typography>Créer <em>{option.data.name}</em></Typography> :
                    <div><AppItem mode='full' type={option.model} id={String(option.id) as ModelId} /></div>
            }</li>;
        }}
    /> : <FormControl fullWidth>
        <InputLabel shrink>{props.textfieldProps?.label ?? ''}</InputLabel>
        <Box sx={{ display: 'flex', flexDirection: 'row', height: 56, paddingLeft: 1, alignItems: 'center' }}>
            <Box sx={{ flexGrow: 1 }}>
                {props.value ? (typeof props.value == 'string' ? props.value : <AppItem mode='compact' type={props.value.type} id={props.value.id} />) : null}
            </Box>
            <Box sx={{ flexShrink: 1 }}>
                <IconButton onClick={() => {
                    if (typeof props.value === 'string') {
                        setInputValue(props.value);
                        const val = newItemToOptions(props.value);
                        setOptions([val]);
                        setValue(val);
                    }
                    setEditing(true);
                }}><Edit /></IconButton>
                {props.onSetEmpty ? <IconButton onClick={() => {
                    props.onSetEmpty && props.onSetEmpty();
                    cancelEditing();
                    /*
                    setAutofocus(true);
                    setEditing(true);
                    */
                }}><Delete /></IconButton> : null}
                {props.onGotoElement ? <IconButton onClick={() => {
                    props.onGotoElement && props.onGotoElement();
                }}><ChevronRight /></IconButton> : null}
            </Box>
        </Box>
    </FormControl>;
};

// This key was created specifically for the demo in mui.com.
// You need to create a new one for your application.
const GOOGLE_MAPS_API_KEY = 'AIzaSyBGpxDRjvCYMtS5CSjFYpECh5uW8i-VX_0';

function loadScript(src: string, position: HTMLElement | null, id: string) {
    if (!position) {
        return;
    }

    const script = document.createElement('script');
    script.setAttribute('async', '');
    script.setAttribute('id', id);
    script.src = src;
    position.appendChild(script);
}

const autocompleteService = { current: null };

interface MainTextMatchedSubstrings {
    offset: number;
    length: number;
}
interface StructuredFormatting {
    main_text: string;
    secondary_text: string;
    main_text_matched_substrings?: readonly MainTextMatchedSubstrings[];
}
interface PlaceType {
    description: string;
    structured_formatting: StructuredFormatting;
}

export const ___DONOTUSE_L_o_cation_Input = () => {
    const [value, setValue] = React.useState<PlaceType | null>(null);
    const [inputValue, setInputValue] = React.useState('');
    const [options, setOptions] = React.useState<readonly PlaceType[]>([]);
    const loaded = React.useRef(false);



    if (typeof window !== 'undefined' && !loaded.current) {
        if (!document.querySelector('#google-maps')) {
            loadScript(
                `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`,
                document.querySelector('head'),
                'google-maps',
            );
        }

        loaded.current = true;
    }

    const fetch = React.useMemo(
        () =>
            debounce(
                (
                    request: { input: string; },
                    callback: (results?: readonly PlaceType[]) => void,
                ) => {
                    (autocompleteService.current as any).getPlacePredictions(
                        request,
                        callback,
                    );
                },
                searchThrottle,
            ),
        [],
    );

    React.useEffect(() => {
        let active = true;

        if (!autocompleteService.current && (window as any).google) {
            autocompleteService.current = new (
                window as any
            ).google.maps.places.AutocompleteService();
        }
        if (!autocompleteService.current) {
            return undefined;
        }

        if (inputValue === '') {
            setOptions(value ? [value] : []);
            return undefined;
        }

        fetch({ input: inputValue }, (results?: readonly PlaceType[]) => {
            if (active) {
                let newOptions: readonly PlaceType[] = [];

                if (value) {
                    newOptions = [value];
                }

                if (results) {
                    newOptions = [...newOptions, ...results];
                }

                setOptions(newOptions);
            }
        });

        return () => {
            active = false;
        };
    }, [value, inputValue, fetch]);

    return (
        <Autocomplete
            id="google-map"
            sx={{ width: 300 }}
            getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.description
            }
            filterOptions={(x) => x}
            options={options}
            autoComplete
            includeInputInList
            filterSelectedOptions
            value={value}
            noOptionsText="No locations"
            onChange={(_event: any, newValue: PlaceType | null) => {
                setOptions(newValue ? [newValue, ...options] : options);
                setValue(newValue);
            }}
            onInputChange={(_event, newInputValue) => {
                setInputValue(newInputValue);
            }}
            renderInput={(params) => (
                <TextField {...params} label="Add a location" fullWidth />
            )}
            renderOption={(props, option) => {
                return (
                    <li {...props}>
                        <Grid container alignItems="center">
                            <Grid item sx={{ display: 'flex', width: 44 }}>
                                <LocationOnIcon sx={{ color: 'text.secondary' }} />
                            </Grid>
                            <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                                <Typography variant="body2" color="text.primary">
                                    {option.structured_formatting.main_text}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {option.structured_formatting.secondary_text}
                                </Typography>
                            </Grid>
                        </Grid>
                    </li>
                );
            }}
        />
    );
};
